/**
 * AWS Cognito Configuration
 * Configure Cognito User Pool settings
 */

import { Amplify } from 'aws-amplify';

export interface CognitoConfig {
  userPoolId: string;
  userPoolClientId: string;
  region: string;
  identityPoolId?: string; // Optional, for unauthenticated access
}

let cognitoConfigCache: CognitoConfig | null = null;

/** Promise so we load `/aws-config.json` once and apply Amplify before login (avoids race with sync init). */
let loadPromise: Promise<void> | null = null;

const loadCognitoConfigFromFile = async (): Promise<void> => {
  try {
    const response = await fetch('/aws-config.json');
    if (response.ok) {
      const config = await response.json();
      if (config.cognitoUserPoolId && config.cognitoUserPoolClientId) {
        cognitoConfigCache = {
          userPoolId: config.cognitoUserPoolId,
          userPoolClientId: config.cognitoUserPoolClientId,
          region: config.cognitoRegion || config.region || 'ap-south-1',
          identityPoolId: config.cognitoIdentityPoolId,
        };
        console.log('Cognito config loaded from aws-config.json');
        return;
      }
    }
  } catch {
    // Cognito optional
  }
  cognitoConfigCache = null;
};

function applyAmplifyConfiguration(): boolean {
  const config = cognitoConfigCache;
  if (!config) {
    return false;
  }

  try {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.userPoolId,
          userPoolClientId: config.userPoolClientId,
        },
      },
    });

    console.log('Cognito initialized successfully');
    return true;
  } catch (error) {
    console.error('Failed to initialize Cognito:', error);
    return false;
  }
}

/**
 * Wait for `/aws-config.json`, then configure Amplify. Call once before rendering the app (see index.tsx).
 */
export const bootstrapCognito = async (): Promise<boolean> => {
  if (!loadPromise) {
    loadPromise = loadCognitoConfigFromFile();
  }
  await loadPromise;
  return applyAmplifyConfiguration();
};

/** Sync init — only works if {@link bootstrapCognito} already completed (e.g. tests). */
export const initializeCognito = (): boolean => {
  if (!cognitoConfigCache) {
    console.log('Cognito not configured. Using mock authentication.');
    return false;
  }
  return applyAmplifyConfiguration();
};

export const isCognitoConfigured = (): boolean => {
  return cognitoConfigCache !== null;
};

export const getCognitoConfigForReference = (): CognitoConfig | null => {
  return cognitoConfigCache;
};
